import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Grid, Row, Col, Content, Text, FlexboxGrid } from 'rsuite';
import Notice from '../../components/user-feedback/Notice';
import PortalHeader from '../../components/layout/PortalHeader';
import FlowPageLayout from '../../components/layout/FlowPageLayout';
import { getAddressWithUnitNumber } from '../../common/utils';
import { selectConfigExpressRegistrationTypeVerbiage } from '../../store/slices/config/configSelectors';
import { FLOWS_ENUM } from '../../common/enums';
import BasicStyledCheckbox from '../../components/form/BasicStyledCheckbox';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import { selectRenewableLicenses } from '../../store/slices/userLicenses/userLicensesSelectors';
import {
  clearBulkRenewalLicenses,
  setBulkRenewalLicenses,
} from '../../store/slices/renew/renewSlice';
import { selectSelectedBulkRenewalLicenses } from '../../store/slices/renew/renewSelectors';
import { getUsersRenewableLicensesAsync } from '../../store/slices/userLicenses/userLicensesThunks';
import { useAppDispatch } from 'src/store/store';

const BulkRenewSelectProperties = () => {
  const dispatch = useAppDispatch();
  const placeNavigate = usePlaceNavigate();

  const [showNoSelectedLicenseError, setShowNoSelectedLicenseError] =
    useState(false);
  const [renewableLicenses, setRenewableLicenses] = useState([]);

  const permitType = useSelector(selectConfigExpressRegistrationTypeVerbiage);
  const { data: selectedUserRenewableLicenses } = useSelector(
    selectRenewableLicenses
  );

  useEffect(() => {
    dispatch(getUsersRenewableLicensesAsync());
  }, [dispatch]);

  const selectedPreSelectedBulkRenewalLicenses = useSelector(
    selectSelectedBulkRenewalLicenses
  );

  useEffect(() => {
    const licenses = selectedUserRenewableLicenses
      .map((l) => ({ ...l }))
      .map(
        (l) =>
          selectedPreSelectedBulkRenewalLicenses.find(
            (item) => item.license === l.license
          ) ?? l
      );
    setRenewableLicenses(licenses ?? []);
  }, [selectedUserRenewableLicenses, selectedPreSelectedBulkRenewalLicenses]);

  const toggleSelectAllLicensesForRenewal = (checked) => {
    let updatedLicenses = renewableLicenses.map((rl) => ({ ...rl }));
    updatedLicenses.forEach((l) => {
      l['selected'] = checked;
    });
    setRenewableLicenses(updatedLicenses);
  };

  const toggleLicenseForRenewal = (license, checked) => {
    let updatedLicenses = renewableLicenses.map((rl) => ({ ...rl }));
    updatedLicenses.forEach((l) => {
      if (l.license === license.license) {
        l['selected'] = checked;
      }
    });
    setRenewableLicenses(updatedLicenses);
  };

  const handleNextClick = () => {
    let valuesToSave = [...renewableLicenses];

    // Check if at least one license has been selected for any year
    const selectedProperties = valuesToSave.filter(
      (license) => license.selected === true
    );

    if (selectedProperties.length > 0) {
      // clear pre-selected payment periods in redux
      dispatch(clearBulkRenewalLicenses());
      // Save data in redux
      dispatch(setBulkRenewalLicenses(selectedProperties));
      return true;
    } else {
      setShowNoSelectedLicenseError(true);
      return false;
    }
  };

  const handleBackClick = () => {
    dispatch(clearBulkRenewalLicenses());
    placeNavigate('/dashboard');
  };
  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader flow={FLOWS_ENUM.BULK_RENEWAL} />
      </FlexboxGrid.Item>

      <FlexboxGrid.Item colspan={24}>
        <Content>
          <FlowPageLayout
            title="Select Properties for Renewal"
            onNext={handleNextClick}
            onBack={() => {
              handleBackClick();
            }}
          >
            <Text className="mb-24">
              Your properties that are eligible for renewal are listed below.
              Please select the properties that you wish to renew at this time.
            </Text>
            <div>
              {renewableLicenses?.length > 0 ? (
                <div
                  className="mb-24"
                  key={`bulk_renewal_table`}
                >
                  <Grid
                    fluid
                    className="tax-table"
                  >
                    <FlexboxGrid
                      className="tax-column-heading-row"
                      align="bottom"
                      as={Row}
                    >
                      <FlexboxGrid.Item
                        as={Col}
                        xs={24}
                        md={2}
                      >
                        <div role="grid">
                          <FlexboxGrid
                            as={Row}
                            align="bottom"
                            gutter={0}
                          >
                            <FlexboxGrid.Item as={Col}>
                              <BasicStyledCheckbox
                                onChange={(checked) => {
                                  toggleSelectAllLicensesForRenewal(checked);
                                }}
                              />
                            </FlexboxGrid.Item>
                            <FlexboxGrid.Item
                              as={Col}
                              mdHidden
                              lgHidden
                              xlHidden
                              xxlHidden
                            >
                              Select/Unselect all
                            </FlexboxGrid.Item>
                          </FlexboxGrid>
                        </div>
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item
                        as={Col}
                        xs={22}
                        xsHidden
                        smHidden
                        mdHidden={false}
                      >
                        <div role="grid">
                          <FlexboxGrid
                            as={Row}
                            align="bottom"
                          >
                            <FlexboxGrid.Item
                              as={Col}
                              xs={22}
                              md={9}
                            >
                              Address
                            </FlexboxGrid.Item>
                            <FlexboxGrid.Item
                              as={Col}
                              xs={12}
                              md={5}
                            >
                              {permitType} Number
                            </FlexboxGrid.Item>
                            <FlexboxGrid.Item
                              as={Col}
                              xs={6}
                              md={5}
                            >
                              Expiration Date
                            </FlexboxGrid.Item>
                            <FlexboxGrid.Item
                              as={Col}
                              xs={6}
                              md={5}
                            >
                              Owner's Name
                            </FlexboxGrid.Item>
                          </FlexboxGrid>
                        </div>
                      </FlexboxGrid.Item>
                    </FlexboxGrid>
                    <div className="tax-license-row-wrapper">
                      {renewableLicenses?.map((license, index) => (
                        <Row
                          key={`license_${index}`}
                          className="tax-license-row"
                        >
                          <Col
                            xs={3}
                            md={2}
                          >
                            <BasicStyledCheckbox
                              onChange={(checked) => {
                                toggleLicenseForRenewal(license, checked);
                              }}
                              checked={license?.selected}
                            />
                          </Col>
                          <Col
                            xs={21}
                            md={22}
                          >
                            <div role="grid">
                              <Row>
                                <Col
                                  xs={22}
                                  md={9}
                                  className="select-properties-address"
                                >
                                  {getAddressWithUnitNumber(
                                    license?.property_address,
                                    license?.unit_number
                                  )}
                                </Col>
                                <Col
                                  xs={12}
                                  md={5}
                                >
                                  {license?.license}
                                </Col>
                                <Col
                                  xs={12}
                                  md={5}
                                >
                                  {license?.expiration_date}
                                </Col>
                                <Col
                                  xs={24}
                                  md={5}
                                >
                                  {license?.owner}
                                </Col>
                              </Row>
                            </div>
                          </Col>
                        </Row>
                      ))}
                    </div>
                  </Grid>
                </div>
              ) : (
                <Notice
                  message="No renewable user licenses available."
                  type="info"
                />
              )}

              {showNoSelectedLicenseError && (
                <Notice
                  message="Please select one or more licenses to continue"
                  className="mb-8"
                />
              )}
            </div>
          </FlowPageLayout>
        </Content>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default BulkRenewSelectProperties;
